export class GoogleDriveService {
  private static instance: GoogleDriveService;
  private accessToken: string | null = null;

  private constructor() {
    try {
      const token = localStorage.getItem('googleDriveAccessToken');
      if (token) {
        this.accessToken = token;
      }
    } catch (error) {
      console.error('Error initializing GoogleDriveService:', error);
    }
  }

  public static getInstance(): GoogleDriveService {
    if (!GoogleDriveService.instance) {
      try {
        GoogleDriveService.instance = new GoogleDriveService();
      } catch (error) {
        console.error('Failed to create GoogleDriveService instance:', error);
        GoogleDriveService.instance = new GoogleDriveService();
      }
    }
    return GoogleDriveService.instance;
  }

  public isAuthenticated(): boolean {
    const token = localStorage.getItem('googleDriveAccessToken');
    if (token && !this.accessToken) {
      // Update instance token if it exists in storage but not in instance
      this.accessToken = token;
    }
    return !!token && !!this.accessToken;
  }

  /**
   * Helper to get current user email if available (for error messages)
   */
  private getCurrentUserEmail(): string | null {
    // Try to get email from localStorage if stored elsewhere, or return null
    // This is just for helpful error messages
    return null;
  }

  public async authenticate(): Promise<void> {
    const CLIENT_ID = '817241114560-6rel2s1l5mp2tkdu935bhpcg99kmms23.apps.googleusercontent.com';
    const redirectUri = window.location.origin + '/';
    const scope = encodeURIComponent('https://www.googleapis.com/auth/drive.readonly');
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${CLIENT_ID}&response_type=token&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${scope}`;
    
    console.log('[GoogleDriveService] Starting authentication with redirect URI:', redirectUri);
    
    return new Promise((resolve, reject) => {
      // Declare popup variable before messageHandler so it's accessible in closure
      let popup: Window | null = null;
      
      // Set up message listener for postMessage from popup
      const messageHandler = (event: MessageEvent) => {
        // Verify origin for security
        if (event.origin !== window.location.origin) {
          return;
        }
        
        if (event.data && event.data.type === 'GOOGLE_OAUTH_TOKEN') {
          window.removeEventListener('message', messageHandler);
          clearInterval(pollInterval);
          
          // Close the popup if it's still open
          if (popup && !popup.closed) {
            try {
              popup.close();
            } catch (e) {
              console.warn('[GoogleDriveService] Could not close popup:', e);
            }
          }
          
          if (event.data.token) {
            localStorage.setItem('googleDriveAccessToken', event.data.token);
            this.accessToken = event.data.token;
            console.log('[GoogleDriveService] Authentication successful (via postMessage)');
            resolve();
          } else if (event.data.error) {
            reject(new Error(`Authentication failed: ${event.data.error}`));
          }
        }
      };
      
      window.addEventListener('message', messageHandler);
      
      // Open OAuth URL in a popup window
      const width = 500;
      const height = 600;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      
      popup = window.open(
        authUrl,
        'Google OAuth',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
      );

      if (!popup) {
        window.removeEventListener('message', messageHandler);
        reject(new Error('Popup blocked. Please allow popups for this site.'));
        return;
      }

      let lastKnownDomain: string | null = null;
      let stuckOnGoogleDomain = false;
      let stuckStartTime: number | null = null;
      let lastAccessibleTime = Date.now();
      const STUCK_TIMEOUT = 15000; // 15 seconds - if stuck on Google domain for this long, likely an error

      // Poll the popup to detect when it redirects back
      const pollInterval = setInterval(() => {
        try {
          // Check if popup was closed manually
          if (popup.closed) {
            clearInterval(pollInterval);
            reject(new Error('Authentication cancelled by user'));
            return;
          }

          // Try to access popup's location (will throw if cross-origin)
          let popupUrl: string;
          try {
            popupUrl = popup.location.href;
            lastAccessibleTime = Date.now();
            
            // Check if we're stuck on Google's error page
            const currentDomain = new URL(popupUrl).hostname;
            if (currentDomain === 'accounts.google.com' || currentDomain.includes('google.com')) {
              // Check if URL contains error indicators
              if (popupUrl.includes('/ServiceLogin') || popupUrl.includes('error') || popupUrl.includes('403') || popupUrl.includes('access_denied')) {
                clearInterval(pollInterval);
                popup.close();
                const errorMsg = `Google OAuth access denied (403). This usually means:\n\n` +
                  `1. The OAuth consent screen is in "Testing" mode and your email (${this.getCurrentUserEmail() || 'your email'}) isn't added as a test user\n` +
                  `2. The redirect URI doesn't match what's configured in Google Cloud Console\n` +
                  `3. The app needs to be verified/published\n\n` +
                  `Current redirect URI: ${redirectUri}\n` +
                  `Expected redirect URI in Google Cloud Console: ${redirectUri}\n\n` +
                  `To fix:\n` +
                  `1. Go to Google Cloud Console → APIs & Services → OAuth consent screen\n` +
                  `2. Check if "Publishing status" is "Testing" - if so, add your email to "Test users"\n` +
                  `3. Or change "Publishing status" to "In production" if ready\n` +
                  `4. Verify redirect URI matches exactly: ${redirectUri}`;
                console.error('[GoogleDriveService] OAuth Error:', errorMsg);
                console.error('[GoogleDriveService] Popup URL:', popupUrl);
                reject(new Error(errorMsg));
                return;
              }
              
              // Track if we're stuck on Google domain
              if (lastKnownDomain === currentDomain) {
                if (stuckStartTime === null) {
                  stuckStartTime = Date.now();
                } else if (Date.now() - stuckStartTime > STUCK_TIMEOUT) {
                  clearInterval(pollInterval);
                  popup.close();
                  const errorMsg = `Authentication appears to be stuck on Google's domain for ${Math.round((Date.now() - stuckStartTime) / 1000)}s. You may be seeing a 403 error page.\n\n` +
                    `Most common cause: OAuth consent screen is in "Testing" mode and you're not a test user.\n\n` +
                    `To fix:\n` +
                    `1. Go to Google Cloud Console → APIs & Services → OAuth consent screen\n` +
                    `2. Check "Publishing status" - if it says "Testing", click "ADD USERS" and add your email\n` +
                    `3. Or change "Publishing status" to "In production"\n\n` +
                    `Current redirect URI: ${redirectUri}\n` +
                    `Make sure this exact URI is in "Authorized redirect URIs": ${redirectUri}`;
                  console.error('[GoogleDriveService] Stuck on Google domain:', popupUrl);
                  console.error('[GoogleDriveService]', errorMsg);
                  reject(new Error(errorMsg));
                  return;
                }
              } else {
                lastKnownDomain = currentDomain;
                stuckStartTime = null;
              }
            } else {
              lastKnownDomain = null;
              stuckStartTime = null;
            }
          } catch (e) {
            // Popup is still on Google's domain (cross-origin), continue polling
            // This is normal during OAuth flow, but if we can't access it for too long, might be an error page
            const timeSinceLastAccess = Date.now() - lastAccessibleTime;
            if (timeSinceLastAccess > STUCK_TIMEOUT && stuckStartTime === null) {
              // We've been unable to access the popup for a while - might be showing an error
              stuckStartTime = Date.now();
            } else if (timeSinceLastAccess < 5000) {
              // Reset stuck timer if we recently accessed it
              stuckStartTime = null;
            }
            return;
          }

            // Check if popup has redirected back to our site
            // Check both the base URL and the full URL with hash
            const baseUrl = popupUrl.split('#')[0];
            if (baseUrl === redirectUri || popupUrl.startsWith(redirectUri)) {
              // Try multiple methods to get the hash
              let hash = '';
              
              // Method 1: Extract from popupUrl string
              const hashIndex = popupUrl.indexOf('#');
              if (hashIndex !== -1) {
                hash = popupUrl.substring(hashIndex + 1);
              }
              
              // Method 2: Try to read from popup.location.hash directly
              if (!hash) {
                try {
                  // Access hash immediately - might work before page fully loads
                  hash = popup.location.hash ? popup.location.hash.substring(1) : '';
                } catch (e) {
                  // Cross-origin or timing issue - this is expected sometimes
                }
              }
              
              // Method 3: Try to inject script into popup to read hash and send via postMessage
              if (!hash && popup && !popup.closed) {
                try {
                  // Check if we can access the popup's document
                  const popupDoc = popup.document;
                  if (popupDoc && popupDoc.readyState === 'loading') {
                    // Page is still loading, inject script to capture hash
                    const script = popupDoc.createElement('script');
                    script.textContent = `
                      (function() {
                        const hash = window.location.hash.substring(1);
                        if (hash && (hash.includes('access_token=') || hash.includes('error='))) {
                          window.opener.postMessage({
                            type: 'GOOGLE_OAUTH_TOKEN',
                            token: hash.includes('access_token=') ? hash.split('&').find(p => p.startsWith('access_token=')).split('=')[1] : null,
                            error: hash.includes('error=') ? hash.split('&').find(p => p.startsWith('error=')).split('=')[1] : null,
                            hash: hash
                          }, '${window.location.origin}');
                        }
                      })();
                    `;
                    popupDoc.head.appendChild(script);
                  } else if (popupDoc && popupDoc.readyState === 'complete') {
                    // Page loaded, try to read hash from current location
                    try {
                      hash = popup.location.hash ? popup.location.hash.substring(1) : '';
                    } catch (e) {
                      // Can't access - try postMessage approach
                      const script = popupDoc.createElement('script');
                      script.textContent = `
                        window.opener.postMessage({
                          type: 'GOOGLE_OAUTH_TOKEN',
                          token: window.location.hash.includes('access_token=') ? window.location.hash.split('&').find(p => p.startsWith('access_token=')).split('=')[1] : null,
                          error: window.location.hash.includes('error=') ? window.location.hash.split('&').find(p => p.startsWith('error=')).split('=')[1] : null,
                          hash: window.location.hash.substring(1)
                        }, '${window.location.origin}');
                      `;
                      popupDoc.head.appendChild(script);
                    }
                  }
                } catch (e) {
                  // Can't access popup document (cross-origin or security restriction)
                  console.log('[GoogleDriveService] Cannot access popup document, will rely on polling:', e);
                }
              }
              
              console.log('[GoogleDriveService] Popup redirected to:', popupUrl);
              console.log('[GoogleDriveService] Extracted hash:', hash ? hash.substring(0, 50) + '...' : '(empty)');
              
              if (hash && hash.includes('access_token=')) {
                clearInterval(pollInterval);
                window.removeEventListener('message', messageHandler);
                
                const accessTokenMatch = hash
                  .split('&')
                  .find(pair => pair.startsWith('access_token='));
                
                if (accessTokenMatch) {
                  const accessToken = decodeURIComponent(accessTokenMatch.split('=')[1]);
                  
                  // Store the token
                  localStorage.setItem('googleDriveAccessToken', accessToken);
                  this.accessToken = accessToken;
                  
                  // Close the popup
                  popup.close();
                  
                  console.log('[GoogleDriveService] Authentication successful');
                  resolve();
                  return;
                }
              }
              
              // Check for errors in the hash
              if (hash && hash.includes('error=')) {
                clearInterval(pollInterval);
                window.removeEventListener('message', messageHandler);
                popup.close();
                
                const errorMatch = hash
                  .split('&')
                  .find(pair => pair.startsWith('error='));
                
                if (errorMatch) {
                  const error = decodeURIComponent(errorMatch.split('=')[1]);
                  const errorDescription = hash
                    .split('&')
                    .find(pair => pair.startsWith('error_description='))
                    ?.split('=')[1];
                  
                  const errorMsg = errorDescription 
                    ? `${error}: ${decodeURIComponent(errorDescription)}`
                    : error;
                  reject(new Error(`Authentication failed: ${errorMsg}`));
                } else {
                  reject(new Error('Authentication failed: Unknown error'));
                }
                return;
              }
              
              // If redirect detected but no hash yet, continue polling
              // (the hash might appear on the next poll, or postMessage will handle it)
              if (!hash) {
                console.log('[GoogleDriveService] Redirect detected but hash not yet available, continuing to poll...');
              }
            }
        } catch (e) {
          // Popup is still on a different domain, continue polling
          // This is expected while the user is on Google's OAuth page
        }
      }, 100); // Poll every 100ms

      // Timeout after 5 minutes
      setTimeout(() => {
        if (!popup.closed) {
          clearInterval(pollInterval);
          window.removeEventListener('message', messageHandler);
          popup.close();
          reject(new Error('Authentication timeout after 5 minutes'));
        }
      }, 300000); // 5 minutes
    });
  }

  /**
   * Extracts file ID from various Google Drive URL formats
   */
  private extractFileId(url: string): string | null {
    // Format 1: https://drive.google.com/file/d/{fileId}/view
    // Format 2: https://drive.google.com/file/d/{fileId}/view?usp=sharing
    // Format 3: https://drive.google.com/uc?export=download&id={fileId}
    // Format 4: https://docs.google.com/document/d/{fileId}/edit (for Google Docs, but we'll handle it)
    
    const patterns = [
      /\/file\/d\/([a-zA-Z0-9_-]+)/,
      /[?&]id=([a-zA-Z0-9_-]+)/,
      /\/document\/d\/([a-zA-Z0-9_-]+)/,
      /\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/,
      /\/presentation\/d\/([a-zA-Z0-9_-]+)/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }

    // If URL is already just a file ID
    if (/^[a-zA-Z0-9_-]+$/.test(url)) {
      return url;
    }

    return null;
  }

  /**
   * Converts Google Drive shared links to direct download URLs
   */
  public async getPermanentLinks(files: { id: string; name: string; link: string }[]): Promise<{ url: string; shareableUrl?: string }[]> {
    if (!this.isAuthenticated()) {
      throw new Error('Not authenticated with Google Drive');
    }

    return files.map(file => {
      const fileId = this.extractFileId(file.id || file.link) || file.id;
      
      console.log(`[GoogleDriveService] Converting URL for file: ${file.name}, fileId: ${fileId}`);
      
      // Use the Drive API v3 endpoint for direct download
      // This requires authentication via Authorization header
      const apiUrl = `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`;
      
      // For sharing purposes, we'll also create a shareable link format
      // But the actual download will use the API endpoint
      const shareableUrl = `https://drive.google.com/file/d/${fileId}/view`;
      
      return { url: apiUrl, shareableUrl };
    });
  }

  /**
   * Downloads file content with progress tracking
   */
  public async getFileContent(
    fileId: string, 
    url: string, 
    onProgress?: (bytesDownloaded: number, totalBytes: number) => void
  ): Promise<Blob> {
    if (!this.isAuthenticated()) {
      throw new Error('Not authenticated with Google Drive');
    }

    // Ensure we have the latest token from localStorage
    const token = localStorage.getItem('googleDriveAccessToken');
    if (!token) {
      throw new Error('Not authenticated with Google Drive');
    }
    this.accessToken = token;

    // Extract file ID from URL if needed
    const actualFileId = this.extractFileId(fileId) || this.extractFileId(url) || fileId;
    
    // Use Drive API v3 endpoint
    const apiUrl = `https://www.googleapis.com/drive/v3/files/${actualFileId}?alt=media`;

    try {
      // Try multiple fetch strategies to improve reliability on mobile browsers
      let response;
      let lastError;
      
      // Strategy 1: Standard fetch with Authorization header
      try {
        response = await fetch(apiUrl, {
          method: 'GET',
          mode: 'cors',
          cache: 'default',
          credentials: 'omit',
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
          }
        });
      } catch (fetchError) {
        lastError = fetchError;
        console.error(`[GoogleDriveService] Strategy 1 failed for ${actualFileId}:`, fetchError);
        
        // Strategy 2: Retry with different cache policy
        try {
          await new Promise(resolve => setTimeout(resolve, 500));
          response = await fetch(apiUrl, {
            method: 'GET',
            mode: 'cors',
            cache: 'force-cache',
            credentials: 'omit',
            headers: {
              'Authorization': `Bearer ${this.accessToken}`
            }
          });
        } catch (retryError) {
          lastError = retryError;
          console.error(`[GoogleDriveService] Strategy 2 failed for ${actualFileId}:`, retryError);
          
          // Strategy 3: Final attempt with minimal options
          try {
            await new Promise(resolve => setTimeout(resolve, 1000));
            response = await fetch(apiUrl, {
              headers: {
                'Authorization': `Bearer ${this.accessToken}`
              }
            });
          } catch (finalError) {
            console.error(`[GoogleDriveService] All fetch attempts failed for ${actualFileId}:`, finalError);
            throw new Error(`Failed to download ${actualFileId}: Network error. Try fewer files or check connection.`);
          }
        }
      }
      
      if (!response.ok) {
        // Handle token expiration
        if (response.status === 401) {
          localStorage.removeItem('googleDriveAccessToken');
          this.accessToken = null;
          throw new Error('Google Drive authentication expired. Please re-authenticate.');
        }
        
        const errorMsg = `Failed to fetch file content: ${response.status} ${response.statusText}`;
        console.error(`[GoogleDriveService] HTTP error for ${actualFileId}:`, errorMsg);
        throw new Error(`Download failed for ${actualFileId}: ${errorMsg}`);
      }

      // If no progress callback is provided, fall back to simple blob download
      if (!onProgress || !response.body) {
        const blob = await response.blob();
        console.log(`[GoogleDriveService] Successfully downloaded ${actualFileId}: ${(blob.size / 1024 / 1024).toFixed(1)}MB`);
        return blob;
      }

      // Real-time progress tracking using ReadableStream
      const reader = response.body.getReader();
      const contentLength = response.headers.get('content-length');
      const totalBytes = contentLength ? parseInt(contentLength, 10) : 0;
      
      let downloadedBytes = 0;
      const chunks: Uint8Array[] = [];
      
      console.log(`[GoogleDriveService] Starting progressive download for ${actualFileId}: ${totalBytes > 0 ? (totalBytes / 1024 / 1024).toFixed(1) + 'MB' : 'unknown size'}`);
      
      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) break;
          
          chunks.push(value);
          downloadedBytes += value.length;
          
          // Report progress if we have a total size
          if (onProgress && totalBytes > 0) {
            onProgress(downloadedBytes, totalBytes);
          }
        }
        
        const blob = new Blob(chunks);
        console.log(`[GoogleDriveService] Successfully downloaded ${actualFileId}: ${(blob.size / 1024 / 1024).toFixed(1)}MB`);
        return blob;
        
      } catch (streamError) {
        console.error(`[GoogleDriveService] Stream reading failed for ${actualFileId}:`, streamError);
        // Fall back to simple blob download if streaming fails
        const blob = await response.blob();
        console.log(`[GoogleDriveService] Fallback download completed for ${actualFileId}: ${(blob.size / 1024 / 1024).toFixed(1)}MB`);
        return blob;
      }
      
    } catch (error) {
      console.error(`[GoogleDriveService] Error fetching file content for ${actualFileId}:`, error);
      throw error;
    }
  }

  /**
   * Extracts file ID from URL (static version for utility use)
   */
  private static extractFileIdStatic(url: string): string | null {
    const patterns = [
      /\/file\/d\/([a-zA-Z0-9_-]+)/,
      /[?&]id=([a-zA-Z0-9_-]+)/,
      /\/document\/d\/([a-zA-Z0-9_-]+)/,
      /\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/,
      /\/presentation\/d\/([a-zA-Z0-9_-]+)/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }

    if (/^[a-zA-Z0-9_-]+$/.test(url)) {
      return url;
    }

    return null;
  }

  /**
   * Converts a Google Drive shared link to a direct download URL
   * This is used for public shared files without authentication
   */
  public static convertToDirectDownloadUrl(url: string): string {
    const fileId = GoogleDriveService.extractFileIdStatic(url);
    if (!fileId) {
      return url; // Return original if we can't extract ID
    }
    
    // For public files, use the uc?export=download format
    // Note: This may require the file to be publicly shared
    return `https://drive.google.com/uc?export=download&id=${fileId}`;
  }
}

